// How numbers and dates look on screen, in one place.

const MONEY = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export function formatMoney(value: string | number | null | undefined): string {
  return value === null || value === undefined ? '–' : MONEY.format(Number(value));
}

/** A date-only value, like a lease or insurance date. Never shifted by the viewer's timezone. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '–';
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', { dateStyle: 'medium' });
}

/** When something happened, in the viewer's own time. The API stores and sends UTC, and most of
 * its timestamps carry no zone marker, so one without a marker is read as UTC. */
/** A month from any date in it, like November 2026. */
export function formatMonth(value: string): string {
  const [year, month] = value.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
}

export function formatDateTime(value: string): string {
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/.test(value);
  return new Date(hasZone ? value : `${value}Z`).toLocaleString('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export function fileSize(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Occupied share of rentable units. Units marked unavailable can't be rented, so they don't count. */
export function occupancyPercent(occupied: number, vacant: number): number | null {
  const rentable = occupied + vacant;
  return rentable === 0 ? null : Math.round((occupied / rentable) * 100);
}

/** Today as YYYY-MM-DD, for a date field's starting value. Call it from an event, not in render. */
export function todayIso(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/** Occupancy for a tile: undefined until loaded, a dash when there's nothing to rent. */
export function formatOccupancy(occupied?: number, vacant?: number): string | undefined {
  if (occupied === undefined || vacant === undefined) return undefined;
  const percent = occupancyPercent(occupied, vacant);
  return percent === null ? '–' : `${percent}%`;
}

const SMALL_WORDS = new Set([
  'a',
  'an',
  'and',
  'as',
  'at',
  'but',
  'by',
  'for',
  'if',
  'in',
  'of',
  'on',
  'or',
  'the',
  'to',
  'vs',
  'with',
]);

/** "Scheduled job runs" to "Scheduled Job Runs", for headings. Leaves ALLCAPS and digits alone. */
export function titleCase(text: string): string {
  const words = text.split(' ');
  return words
    .map((word, index) => {
      const edge = index === 0 || index === words.length - 1;
      if (!edge && SMALL_WORDS.has(word.toLowerCase())) return word.toLowerCase();
      return word
        .split('-')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join('-');
    })
    .join(' ');
}
